import { Address, AddressSuggestion, GoogleMapAutocompletePrediction } from 'src/types';
import { transformPlaceDetails, getStreetAddress } from 'src/functions/google-maps';
import { getCountryRegions } from 'src/functions/address';

const HTML_ESCAPE_MAP: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (s: string) => s.replace(/[&<>"']/g, c => HTML_ESCAPE_MAP[c]);
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Highlights the matching parts of a text based on a query.
 */
export function highlightMatch(text: string, query: string): string {
  if (!text || !query) {
    return escapeHtml(text || '');
  }

  const safeText = escapeHtml(text);
  const words = query
    .split(/\s+/)
    .filter(word => word)
    .map(escapeRegex);

  if (words.length === 0) {
    return safeText;
  }

  try {
    const regex = new RegExp(`(${words.join('|')})`, 'gi');
    return safeText.replace(regex, '<strong>$1</strong>');
  } catch (error) {
    return safeText;
  }
}

/**
 * The site locale in BCP-47 format ("en-US") — WordPress stores it with an underscore ("en_US").
 */
const getLanguageCode = (): string => (window?.scData?.locale || document.documentElement.lang || 'en').replace(/_/g, '-');

/**
 * Create a Places API session token. Groups autocomplete keystrokes and the final
 * Place Details call into one billing session. Falls back for insecure contexts
 * where crypto.randomUUID is unavailable.
 */
export const createSessionToken = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Fetches address predictions from the Google Places Autocomplete API.
 *
 * Autocomplete (not Text Search) is required here — Text Search matches businesses
 * and POIs, so partial addresses returned shops instead of street addresses.
 */
export async function fetchAddressSuggestions(input: string, country: string | null, sessionToken: string, signal?: AbortSignal): Promise<Array<AddressSuggestion>> {
  const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': window?.scData?.google_map_api_key,
    },
    body: JSON.stringify({
      input,
      sessionToken,
      languageCode: getLanguageCode(),
      // Hard restriction — predictions outside the selected checkout country are excluded.
      ...(country ? { includedRegionCodes: [country] } : {}),
    }),
    signal,
  });

  const data = await response.json();

  if (data?.error?.message) {
    throw new Error(data.error.message);
  }

  return (data?.suggestions || []).flatMap((suggestion: { placePrediction?: GoogleMapAutocompletePrediction }) => {
    const prediction = suggestion?.placePrediction;
    if (!prediction?.placeId) return [];
    return [
      {
        displayName: prediction?.structuredFormat?.mainText?.text ?? prediction?.text?.text ?? input,
        fullDisplayName: prediction?.text?.text ?? input,
        placeId: prediction.placeId,
      },
    ];
  });
}

/**
 * Returns a full replacement address — preserves recipient name only, does not merge with the
 * previous address. Merging would leak stale line_2/city/postal_code when the new place omits them.
 */
export function buildReplacementAddressFromPlace(
  place: AddressSuggestion,
  regions: Array<{ value: string; label: string }>,
  previousName: string | null | undefined,
): Partial<Address> {
  if (!place?.addressComponents?.length) {
    throw new Error('Place details not found.');
  }

  const placeDetails = transformPlaceDetails(place.addressComponents, regions);
  const line1 = getStreetAddress(place.addressComponents, place.addressLines) || place.displayName || '';

  return {
    name: previousName ?? null,
    line_1: line1,
    line_2: placeDetails.line_2 ?? null,
    city: placeDetails.city ?? null,
    state: placeDetails.state ?? null,
    postal_code: placeDetails.postal_code ?? null,
    country: placeDetails.country ?? null,
  };
}

/**
 * Fetches place details from the Google Places API and transforms them into an address.
 *
 * Passing the autocomplete session token here closes the billing session — the token
 * is invalid afterwards, so the caller must discard it.
 */
export async function fetchPlaceDetails(
  suggestion: AddressSuggestion,
  address: Partial<Address>,
  regions: Array<{ value: string; label: string }>,
  sessionToken?: string,
): Promise<{ updatedAddress: Partial<Address>; updatedRegions: Array<{ value: string; label: string }> }> {
  if (!suggestion?.placeId) {
    throw new Error('Place details not found.');
  }

  const params = new URLSearchParams({
    languageCode: getLanguageCode(),
    ...(sessionToken ? { sessionToken } : {}),
  });

  const response = await fetch(`https://places.googleapis.com/v1/places/${suggestion.placeId}?${params.toString()}`, {
    headers: {
      'X-Goog-Api-Key': window?.scData?.google_map_api_key,
      'X-Goog-FieldMask': 'addressComponents,postalAddress.addressLines',
    },
  });

  const data = await response.json();

  if (data?.error?.message) {
    throw new Error(data.error.message);
  }

  const addressComponents = data?.addressComponents || [];
  const place: AddressSuggestion = { ...suggestion, addressComponents, addressLines: data?.postalAddress?.addressLines || [] };
  const country = addressComponents.find(component => component.types?.includes('country'))?.shortText || null;
  const updatedRegions = address?.country !== country ? await getCountryRegions(country) : regions;

  return {
    updatedAddress: buildReplacementAddressFromPlace(place, updatedRegions, address.name),
    updatedRegions,
  };
}
